import { Text } from "@solomon-labs/design/components/ui";
import React from "react";

export type BadgeTone =
    | "default"
    | "sand"
    | "septenary"
    | "primary"
    | "secondary"
    | "senary"
    | "nonary";

export interface BadgeProps {
    children: React.ReactNode;
    /**
     * Surface tone. `default` is the neutral grey chip; `sand`
     * distinguishes funding-account chips from region chips; `septenary`
     * marks a program target chip (graph-palette septenary).
     *
     * `primary` / `secondary` / `senary` / `nonary` are the light end of
     * the v3 graph palette, for **categorical** encoding — one tone per
     * category in a set, so a reader can tell kinds apart at a glance.
     * They are deliberately the light swatches only: the chip's label is
     * `text-foreground-base`, so a dark graph tone (quinary, denary,
     * quaternary) would fail contrast and is not offered.
     */
    tone?: BadgeTone;
}

/** Tone → background utility. Kept as a lookup rather than a nested
 *  ternary now that there are seven tones. */
const TONE_BG: Record<BadgeTone, string> = {
    default: "bg-surface-sunken",
    sand: "bg-sand",
    septenary: "bg-graph-septenary",
    primary: "bg-graph-primary",
    secondary: "bg-graph-secondary",
    senary: "bg-graph-senary",
    nonary: "bg-graph-nonary"
};

/**
 * Badge — small square chip (square corners per brand) with a bordered
 * surface and `fine-print` label text.
 *
 * Generic Component: knows no Solomon/YaaS entity — a bordered tone-tinted
 * chip whose content is a slot. `tone="sand"` distinguishes funding-account
 * chips from the (grey) region chips; `tone="septenary"` marks a program
 * target chip.
 *
 * @version 1.1.0
 * @published 2026-08-21
 * @history
 *   1.1.0 — Add the light graph-palette tones (`primary`, `secondary`,
 *           `senary`, `nonary`) for categorical encoding, and move the
 *           tone→background mapping into a lookup. Backward-compatible:
 *           existing tones and the `default` fallback are unchanged.
 *   1.0.0 — Initial publish. Promoted verbatim (pixel-identical) from the
 *           client Programs page-local `programChips.Badge`, shared by the
 *           ProgramDetail title block and the FundingRow account list.
 *
 * @see Badge.intent.md   policy + recipes
 * @see https://solomon-wiki.netlify.app/technical/analysis/component-anatomy/shared/components/Badge.layout.html intended layout
 */
export function Badge({
    children,
    tone = "default"
}: BadgeProps): React.ReactElement {
    return (
        <span
            className={`inline-flex items-center border border-line-base px-2 py-0.5 ${TONE_BG[tone]}`}
        >
            <Text
                variant="fine-print"
                render={<span />}
                className="text-foreground-base"
            >
                {children}
            </Text>
        </span>
    );
}
